import { valueAt } from './scales';
import type { SeriesPoint } from './seriesTypes';

export interface StackBand {
    t: number;
    y0: number;
    y1: number;
}

export interface StackLayer {
    id: string;
    band: StackBand[];
}

interface StackInput {
    id: string;
    points: readonly SeriesPoint[];
}

function timeGrid(lists: readonly (readonly SeriesPoint[])[]): number[] {
    const times = new Set<number>();
    for (const points of lists) for (const p of points) times.add(p.t);
    return [...times].sort((a, b) => a - b);
}

function shareAt(points: readonly SeriesPoint[], t: number): number {
    return Math.max(0, valueAt(points, t) ?? 0);
}

export const MIN_LABEL_SHARE = 0.1;
export const MIN_LABEL_PIXELS = 22;

export function bandAt(
    band: readonly StackBand[],
    t: number
): StackBand | null {
    let found: StackBand | null = null;
    for (const point of band) {
        if (point.t > t) break;
        found = point;
    }
    return found;
}

export function othersPoints(
    lists: readonly (readonly SeriesPoint[])[]
): SeriesPoint[] {
    return timeGrid(lists).map(t => ({
        t,
        v: Math.max(
            0,
            1 - lists.reduce((sum, points) => sum + shareAt(points, t), 0)
        ),
    }));
}

export function stackLayers(inputs: readonly StackInput[]): StackLayer[] {
    const grid = timeGrid(inputs.map(i => i.points));
    const base = grid.map(() => 0);
    const scale = grid.map(t => {
        const total = inputs.reduce(
            (sum, input) => sum + shareAt(input.points, t),
            0
        );
        return total > 1 ? 1 / total : 1;
    });
    return inputs.map(input => ({
        id: input.id,
        band: grid.map((t, i) => {
            const y0 = base[i];
            const y1 = y0 + shareAt(input.points, t) * scale[i];
            base[i] = y1;
            return { t, y0, y1 };
        }),
    }));
}
